import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Plus, Check, Trash2, ListChecks, Loader2 } from "lucide-react";

const inputCls = "flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export const ServiceSteps = ({ ticketId, steps: initialSteps, canEdit = true, onChange }) => {
  const { t } = useLang();
  const [steps, setSteps] = useState(initialSteps || []);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => setSteps(initialSteps || []), [initialSteps]);

  const notify = (list) => { setSteps(list); onChange && onChange(list); };

  const addStep = async () => {
    const lbl = newLabel.trim();
    if (!lbl) return;
    setBusy(true);
    try {
      const step = await api.post(`/tickets/${ticketId}/steps`, { label: lbl });
      notify([...steps, step]);
      setNewLabel("");
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const toggle = async (s) => {
    const next = !s.done;
    try {
      const r = await api.put(`/tickets/${ticketId}/steps/${s.id}`, { done: next });
      notify(r.service_steps);
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
  };

  const remove = async (s) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.del(`/tickets/${ticketId}/steps/${s.id}`);
      notify(steps.filter((x) => x.id !== s.id));
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
  };

  const doneCount = steps.filter((s) => s.done).length;
  const progressPct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  return (
    <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="service-steps">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("serviceSteps") || "Service Steps"}</span>
          {steps.length > 0 && <span className="text-xs text-slate-400">{doneCount}/{steps.length} • {progressPct}%</span>}
        </div>
      </div>

      {steps.length > 0 && (
        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      )}

      <ul className="space-y-1.5">
        {steps.map((s) => (
          <li key={s.id} data-testid={`step-${s.id}`} className={`flex items-center gap-2.5 rounded-lg px-3 py-2 border ${s.done ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}>
            <button
              data-testid={`step-toggle-${s.id}`}
              onClick={() => canEdit && toggle(s)}
              disabled={!canEdit}
              className={`w-5 h-5 rounded flex items-center justify-center border-2 shrink-0 ${s.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 hover:border-slate-500"} ${!canEdit ? "cursor-not-allowed opacity-60" : ""}`}
            >
              {s.done && <Check className="w-3 h-3" />}
            </button>
            <span className={`text-sm flex-1 ${s.done ? "line-through text-slate-400" : "text-slate-800"}`}>{s.label}</span>
            {s.done && s.done_by_name && (
              <span className="text-[11px] text-slate-400 hidden sm:inline">{s.done_by_name}{s.done_at ? ` • ${s.done_at.slice(0, 16).replace("T", " ")}` : ""}</span>
            )}
            {canEdit && (
              <button data-testid={`step-remove-${s.id}`} onClick={() => remove(s)} className="p-1 rounded hover:bg-red-50 hover:text-red-600 text-slate-400"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </li>
        ))}
        {steps.length === 0 && <li className="text-xs text-slate-400 text-center py-3">{t("serviceStepsEmpty") || "No steps yet — add the workflow (e.g. Visit, Diagnostic, Repair)"}</li>}
      </ul>

      {canEdit && (
        <div className="flex items-center gap-2">
          <input
            data-testid="step-new-input"
            value={newLabel}
            placeholder={t("newStepPlaceholder") || "e.g. Visit, Diagnostic, Repair, QA"}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addStep(); }}
            className={inputCls}
          />
          <button
            data-testid="step-add-btn"
            onClick={addStep}
            disabled={busy || !newLabel.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white text-sm font-medium px-3 py-2 hover:bg-slate-800 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>
      )}
    </div>
  );
};

export default ServiceSteps;
